import { EntrGroups } from "@/auth/entra-groups";

/**
 * Process raw group IDs into user-friendly role names
 * @param groups - Array of group IDs 
 * @returns Array of processed role names (Agent, Passenger, TravelProfessional)
 */
export function processGroups(groups: string[] = []): string[] {
  if (!groups || groups.length === 0) return [];

  // Create a map for faster lookups
  const groupHashTable = new Map<string, boolean>(
    groups.map(group => [group, true])
  );

  const result: string[] = [];

  // Check if any of the Passenger IDs match
  if (EntrGroups.Passenger.some(id => groupHashTable.has(id))) {
    result.push("Passenger");
  }

  // Check if any of the Agent IDs match
  if (EntrGroups.Agent.some(id => groupHashTable.has(id))) {
    result.push("Agent");
  }

  // Check if any of the TravelProfessional IDs match
  if (EntrGroups.TravelProfessional.some(id => groupHashTable.has(id))) {
    result.push("TravelProfessional");
  }

  return result;
}

/**
 * Processes user groups from API response or provided groups
 * @param userGroups - User groups from context/props
 * @returns Processed user groups array
 */
export function processUserGroupsFromApiResponse(
  userGroups: string[] = []
): string[] {
  return processGroups(userGroups);
}

/**
 * Helper function to check if the user has a specific role
 */
export function hasRole(userGroups: string[], role: string): boolean {
  return userGroups.includes(role);
}

/**
 * Determine if the user is a passenger
 */
export function isPassenger(userGroups: string[]): boolean {
  return hasRole(userGroups, "Passenger");
}

/**
 * Determine if the user is an agent
 */
export function isAgent(userGroups: string[]): boolean {
  return hasRole(userGroups, "Agent");
}

/**
 * Determine if the user is a travel professional
 */
export function isTravelProfessional(userGroups: string[]): boolean {
  return hasRole(userGroups, "TravelProfessional");
}

/**
 * Determine if the user is an agent or professional
 */
export function isAgentOrProfessional(userGroups: string[]): boolean {
  return isAgent(userGroups) || isTravelProfessional(userGroups);
}

/**
 * Helper function to store original site origin in sessionStorage (instead of localStorage)
 */
export function storeOriginalSiteOrigin(): void {
  if (typeof window !== "undefined") {
    sessionStorage.setItem("originalSiteOrigin", window.location.href);
  }
}

/**
 * Helper function to get original site origin from sessionStorage
 */
export function getOriginalSiteOrigin(): string {
  if (typeof window !== "undefined") {
    return sessionStorage.getItem("originalSiteOrigin") || window.location.origin;
  }
  return "";
}

/**
 * Handle group-based redirection
 * @param userGroups - Array of user roles ("Agent", "Passenger", "TravelProfessional")
 * @param travelerRedirect - URL to redirect Passenger users to
 * @param agentRedirect - URL to redirect Agent users to
 */
export function handleGroupRedirection(
  userGroups: string[],
  travelerRedirect: string,
  agentRedirect: string
): void {
  if (isAgent(userGroups)) {
    const currentUrl = window.location.pathname;
    const redirectUrl = currentUrl.endsWith("/order-brochures")
      ? currentUrl.replace("/order-brochures", "/travel-advisors/order-brochures")
      : agentRedirect;
    window.open(redirectUrl, "_self");
    return;
  }
  if (isTravelProfessional(userGroups)) {
    return;
  }
  if (isPassenger(userGroups)) {
    window.open(travelerRedirect, "_blank");
  }
}